import { useMemo, useState } from "react";

import ChevronRight from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import type { SxProps, Theme } from "@mui/material/styles";

import { differenceInCalendarDays, format } from "date-fns";

import type { FoundSlot } from "./use-find-next-available.hook";

export const INITIAL_VISIBLE_DAYS = 7;
export const INITIAL_VISIBLE_SLOTS_PER_PROVIDER = 12;
// WCAG 2.5.5 (AAA) pointer-target floor. The slot was a 30px MUI Chip, which
// reads as one of the app's passive tag chips rather than something to press —
// staff on the older end of the demographic were treating the results pane as
// a printout of times. Don't shrink this back.
export const SLOT_MIN_HEIGHT = 44;
const SLOT_HOVER_TRANSITION =
  "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease";

export interface ProviderMeta {
  id: number;
  name: string;
  // A MUI color object from STAFF_COLORS (e.g. blue) — [500] for dots,
  // [400] for the loading-overlay bars. Typed loose like the existing
  // AvailabilitySlot's `color` prop.
  color: any;
}

export type ResultsGroupBy = "time" | "provider";

interface SlotResultsListProps {
  slots: FoundSlot[];
  groupBy: ResultsGroupBy;
  providers: ProviderMeta[];
  /** Badges only make sense when the type uses appointment blocks. */
  showSourceBadges: boolean;
  onSelectSlot: (slot: FoundSlot) => void;
}

const dayKey = (slot: FoundSlot) =>
  format(new Date(slot.startTime), "yyyy-MM-dd");

// Results span up to 93 days — and with a future start date, cross into next
// year — so a day label always carries the month ("Thu 15" is ambiguous across
// three months) and adds the year once it differs from the current one.
export function formatSlotDayLabel(
  date: Date,
  weekdayFormat: "EEE" | "EEEE" = "EEE"
): string {
  const needsYear = date.getFullYear() !== new Date().getFullYear();
  return format(date, `${weekdayFormat}, MMM d${needsYear ? ", yyyy" : ""}`);
}

function relativeDayLabel(date: Date): string {
  const days = differenceInCalendarDays(date, new Date());
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  // Far-out searches (a 6-month recall) would read "in 183 days" — the month
  // approximation is what staff actually think in.
  if (days >= 60) return `~${Math.round(days / 30)} months out`;
  return `in ${days} days`;
}

// One spelling of the source for both the badge and the tooltip — the tooltip
// doubles as the chip's accessible name, so the two must not drift.
function sourceLabel(source: FoundSlot["source"]): string | null {
  if (!source) return null;
  return source === "dedicated" ? "Dedicated" : "Open time";
}

function SourceBadge({ source }: { source: FoundSlot["source"] }) {
  const label = sourceLabel(source);
  if (!label) return null;
  const isDedicated = source === "dedicated";
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        borderRadius: "4px",
        px: 0.5,
        py: 0.1,
        color: isDedicated ? "primary.dark" : grey[600],
        bgcolor: isDedicated ? "primary.light" : grey[200]
      }}
    >
      {label}
    </Typography>
  );
}

/**
 * "Book Thu, Aug 13 at 2:15pm with Dr. Kiyani" — spells out the action, and
 * restates the day the merged timeline only carries in its group header.
 *
 * MUI's Tooltip publishes this string as the chip's `aria-label`, so it is the
 * whole accessible name: anything the sighted chip shows and this omits is
 * invisible to a screen reader. That's why the source rides along whenever the
 * badge is on screen.
 */
function slotTooltipTitle(
  slot: FoundSlot,
  providerName: string | undefined,
  withSource: boolean
): string {
  const date = new Date(slot.startTime);
  const when = `${formatSlotDayLabel(date)} at ${format(date, "h:mmaaa")}`;
  const base = providerName
    ? `Book ${when} with ${providerName}`
    : `Book ${when}`;
  const source = withSource ? sourceLabel(slot.source) : null;
  return source ? `${base} (${source})` : base;
}

// The hover state is the affordance: an outlined slot inverts to a solid
// primary fill rather than MUI's default 4%-black wash, which is effectively
// invisible against a bordered chip.
const slotSx = (isPrimary: boolean): SxProps<Theme> => ({
  height: "auto",
  minHeight: SLOT_MIN_HEIGHT,
  // A pill is the app's tag shape; a soft rectangle is its button shape.
  borderRadius: 2,
  py: 0.75,
  transition: SLOT_HOVER_TRANSITION,
  "& .MuiChip-label": { px: 1.25, display: "flex" },
  ...(isPrimary
    ? {
        boxShadow: 2,
        "&:hover": { bgcolor: "primary.dark", boxShadow: 4 }
      }
    : {
        borderWidth: 1.5,
        borderColor: "primary.main",
        bgcolor: "background.paper",
        boxShadow: 1,
        "&:hover": {
          bgcolor: "primary.main",
          borderColor: "primary.main",
          boxShadow: 4,
          // Scoped to the label spans on purpose — a blanket
          // `.MuiTypography-root` rule would also recolor SourceBadge's text
          // and wreck its contrast against its own background.
          "& .fna-slot-time, & .fna-slot-provider, & .fna-slot-go": {
            color: "primary.contrastText",
            opacity: 1
          },
          // Keeps the provider's calendar colour legible on the dark fill.
          "& .fna-slot-dot": { boxShadow: "0 0 0 2px rgba(255,255,255,0.9)" }
        }
      }),
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2
  }
});

function SlotChip({
  slot,
  provider,
  label,
  tooltipProviderName,
  isPrimary = false,
  showSourceBadges,
  onSelectSlot
}: {
  slot: FoundSlot;
  provider: ProviderMeta | undefined;
  label: string;
  /**
   * Named separately from `provider` because the by-provider view renders no
   * dot on the chip but still wants its section's provider in the tooltip.
   */
  tooltipProviderName: string | undefined;
  /** The single soonest opening — rendered filled, and carrying the verb. */
  isPrimary?: boolean;
  showSourceBadges: boolean;
  onSelectSlot: (slot: FoundSlot) => void;
}) {
  return (
    <Tooltip
      title={slotTooltipTitle(slot, tooltipProviderName, showSourceBadges)}
      enterDelay={400}
      disableInteractive
    >
      <Chip
        data-testid="find-next-available-slot"
        clickable
        onClick={() => onSelectSlot(slot)}
        variant={isPrimary ? "filled" : "outlined"}
        color={isPrimary ? "primary" : "default"}
        sx={slotSx(isPrimary)}
        label={
          <Stack direction="row" spacing={0.75} alignItems="center">
            {provider && (
              <Box
                component="span"
                className="fna-slot-dot"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: provider.color[500],
                  flexShrink: 0
                }}
              />
            )}
            <Typography
              component="span"
              className="fna-slot-time"
              sx={{ fontSize: 14, fontWeight: 600 }}
            >
              {label}
            </Typography>
            {provider && (
              <Typography
                component="span"
                className="fna-slot-provider"
                sx={{
                  fontSize: 13,
                  color: isPrimary ? "inherit" : "text.secondary",
                  opacity: isPrimary ? 0.85 : 1
                }}
              >
                {provider.name}
              </Typography>
            )}
            {showSourceBadges && <SourceBadge source={slot.source} />}
            {/* Rendered at rest rather than revealed on hover: the cue has to
                land for someone who never hovers, and a width transition here
                would reflow the whole wrapping grid. */}
            <ChevronRight
              className="fna-slot-go"
              sx={{
                fontSize: 16,
                flexShrink: 0,
                color: isPrimary ? "inherit" : "primary.main",
                // Not decoration — it's one of the cues carrying the "this is
                // pressable" read, so it has to clear WCAG 1.4.11's 3:1 for
                // non-text indicators. 0.55 landed at ~2.3:1.
                opacity: isPrimary ? 0.9 : 0.8,
                transition: SLOT_HOVER_TRANSITION
              }}
            />
          </Stack>
        }
      />
    </Tooltip>
  );
}

function MergedTimelineView({
  slots,
  providers,
  showSourceBadges,
  onSelectSlot
}: Omit<SlotResultsListProps, "groupBy">) {
  const [visibleDays, setVisibleDays] = useState(INITIAL_VISIBLE_DAYS);
  const providerById = useMemo(
    () => new Map(providers.map((provider) => [provider.id, provider])),
    [providers]
  );

  const dayGroups = useMemo(() => {
    const groups = new Map<string, FoundSlot[]>();
    slots.forEach((slot) => {
      const key = dayKey(slot);
      groups.set(key, [...(groups.get(key) ?? []), slot]);
    });
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  // Derived from the times rather than taken as `dayGroups[0][1][0]`: the
  // dialog prints its own "· earliest …" line from the same set, and the two
  // must not disagree if a caller ever hands us unsorted slots. Still an
  // element of `slots`, so `daySlots` matches it by reference below.
  // The one filled slot teaches the pattern for the other ~194 without anyone
  // having to hover first, and doubles as the "just give me the soonest" fast
  // path most of these searches actually want.
  const earliestSlot = useMemo(
    () =>
      slots.reduce<FoundSlot | undefined>((earliest, slot) => {
        if (!earliest) return slot;
        return new Date(slot.startTime) < new Date(earliest.startTime)
          ? slot
          : earliest;
      }, undefined),
    [slots]
  );

  return (
    <Stack spacing={0}>
      {dayGroups.slice(0, visibleDays).map(([key, daySlots]) => {
        const date = new Date(daySlots[0].startTime);
        return (
          <Box key={key} data-testid="find-next-available-day">
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ bgcolor: grey[100], px: 1.5, py: 0.75 }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                {formatSlotDayLabel(date, "EEEE")}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {relativeDayLabel(date)}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              sx={{ px: 1.5, py: 1.25 }}
            >
              {daySlots.map((slot) => {
                const provider = providerById.get(slot.staffMember?.id);
                const isPrimary = slot === earliestSlot;
                const time = format(new Date(slot.startTime), "h:mmaaa");
                return (
                  <SlotChip
                    key={`${slot.staffMember?.id}-${slot.startTime}`}
                    slot={slot}
                    provider={provider}
                    label={isPrimary ? `Book ${time}` : time}
                    tooltipProviderName={provider?.name}
                    isPrimary={isPrimary}
                    showSourceBadges={showSourceBadges}
                    onSelectSlot={onSelectSlot}
                  />
                );
              })}
            </Stack>
          </Box>
        );
      })}
      {dayGroups.length > visibleDays && (
        <Button
          size="small"
          onClick={() =>
            setVisibleDays((count) => count + INITIAL_VISIBLE_DAYS)
          }
          sx={{ alignSelf: "center", my: 1 }}
        >
          Show more days
        </Button>
      )}
    </Stack>
  );
}

function ByProviderView({
  slots,
  providers,
  showSourceBadges,
  onSelectSlot
}: Omit<SlotResultsListProps, "groupBy">) {
  // providerId -> how many slots are revealed (chronological order).
  const [visibleCounts, setVisibleCounts] = useState<Record<number, number>>(
    {}
  );

  const slotsByProvider = useMemo(() => {
    const groups = new Map<number, FoundSlot[]>();
    slots.forEach((slot) => {
      const id = slot.staffMember?.id;
      groups.set(id, [...(groups.get(id) ?? []), slot]);
    });
    return groups;
  }, [slots]);

  return (
    <Stack spacing={0}>
      {providers.map((provider) => {
        const providerSlots = slotsByProvider.get(provider.id) ?? [];
        const visibleCount =
          visibleCounts[provider.id] ?? INITIAL_VISIBLE_SLOTS_PER_PROVIDER;
        const nextSlot = providerSlots[0];
        return (
          <Box key={provider.id} data-testid="find-next-available-provider">
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ bgcolor: grey[50], px: 1.5, py: 0.75 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: provider.color[500]
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                {provider.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.secondary",
                  ml: "auto !important"
                }}
              >
                {nextSlot
                  ? `Next: ${formatSlotDayLabel(new Date(nextSlot.startTime))} · ${format(new Date(nextSlot.startTime), "h:mmaaa")}`
                  : "No availability found"}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              sx={{ px: 1.5, py: 1.25 }}
            >
              {/* No primed slot here: each section already leads with its own
                  provider's "Next: …", so a filled chip per provider would
                  read as three competing calls to action. */}
              {providerSlots.slice(0, visibleCount).map((slot) => (
                <SlotChip
                  key={`${slot.staffMember?.id}-${slot.startTime}`}
                  slot={slot}
                  provider={undefined}
                  label={`${formatSlotDayLabel(new Date(slot.startTime))} · ${format(new Date(slot.startTime), "h:mmaaa")}`}
                  tooltipProviderName={provider.name}
                  showSourceBadges={showSourceBadges}
                  onSelectSlot={onSelectSlot}
                />
              ))}
              {providerSlots.length > visibleCount && (
                <Button
                  size="small"
                  onClick={() =>
                    setVisibleCounts((counts) => ({
                      ...counts,
                      [provider.id]:
                        visibleCount + INITIAL_VISIBLE_SLOTS_PER_PROVIDER
                    }))
                  }
                >
                  Show more
                </Button>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

/**
 * Search results for Find Next Available: a merged day-grouped timeline
 * ("what's the soonest with anyone?") or a per-provider grouping ("compare
 * providers"), toggled by the parent. Slot times are shown in the viewer's
 * local timezone — `formatStaffAvailability` already converted them from the
 * clinic's timezone, exactly like the in-form availability panel.
 */
export default function SlotResultsList({
  slots,
  groupBy,
  providers,
  showSourceBadges,
  onSelectSlot
}: SlotResultsListProps) {
  if (groupBy === "provider") {
    return (
      <ByProviderView
        slots={slots}
        providers={providers}
        showSourceBadges={showSourceBadges}
        onSelectSlot={onSelectSlot}
      />
    );
  }
  return (
    <MergedTimelineView
      slots={slots}
      providers={providers}
      showSourceBadges={showSourceBadges}
      onSelectSlot={onSelectSlot}
    />
  );
}
